import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Accordion } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { getFollowupListAction } from "../../../../../store/actions/ServiceFollowUpAction";
import { FaFile, FaFilePdf, FaFileImage, FaFileAlt, FaExternalLinkAlt } from "react-icons/fa";

const QuestionnaireSection = ({ prescriptionId }) => {
  const dispatch = useDispatch();
  const [activeAccordion, setActiveAccordion] = useState(null);

  const followupListData = useSelector(
    (state) => state.serviceFollowup?.followupListData?.data || []
  );
  const followupListLoading = useSelector(
    (state) => state.serviceFollowup?.followupListLoading
  );
  const followupListError = useSelector(
    (state) => state.serviceFollowup?.followupListError
  );

  // Helper function to check if a string is a valid URL
  const isValidUrl = (string) => {
    if (!string) return false;
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  // Helper function to check if string is base64 data URL
  const isDataUrl = (string) => {
    return string && (string.startsWith('data:application/pdf') || 
                     string.startsWith('data:image/') ||
                     string.startsWith('data:application/'));
  };

  // Helper function to get file icon based on URL/file type
  const getFileIcon = (url) => {
    if (url && (url.includes(".pdf") || url.includes("application/pdf"))) {
      return <FaFilePdf className="text-danger me-2" size={18} />;
    }
    if (url && (url.includes(".jpg") || url.includes(".jpeg") || url.includes(".png") || url.includes(".gif") || url.includes(".webp"))) {
      return <FaFileImage className="text-success me-2" size={18} />;
    }
    if (url && (url.includes(".doc") || url.includes(".docx"))) {
      return <FaFileAlt className="text-primary me-2" size={18} />;
    }
    return <FaFile className="text-primary me-2" size={18} />;
  };

  // Helper function to get file name from URL
  const getFileName = (url) => {
    try {
      if (isDataUrl(url)) {
        if (url.includes('application/pdf')) return 'Document.pdf';
        if (url.includes('image/png')) return 'Image.png';
        if (url.includes('image/jpeg')) return 'Image.jpg';
        if (url.includes('image/jpg')) return 'Image.jpg';
        return 'File';
      }
      
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split("/").pop();
      if (fileName && fileName.length > 0) {
        return decodeURIComponent(fileName).substring(0, 50);
      }
      return "View File";
    } catch {
      return "View File";
    }
  };

  // Function to render answer with file handling
  const renderAnswer = (answer, questionType) => {
    if (!answer) return <p className="mb-0 text-muted">No answer</p>;
    
    // Check if it's a file input question with URL or data URL
    if (questionType === "File Input" && (isValidUrl(answer) || isDataUrl(answer))) {
      return (
        <div className="mb-2">
          <a 
            href={answer} 
            target="_blank" 
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center text-decoration-none p-2 border rounded bg-light"
            style={{ cursor: "pointer", transition: "all 0.2s" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e9ecef";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
            }}
          >
            {getFileIcon(answer)}
            <span className="me-2">{getFileName(answer)}</span>
            <FaExternalLinkAlt size={12} className="text-muted" />
          </a>
        </div>
      );
    }
    
    // For other question types, show regular answer
    return <p className="mb-0">{answer}</p>;
  };

  // Function to format date
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date)) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Function to fetch followup data
  const fetchFollowupData = async () => {
    try {
      console.log("Fetching followup data for prescriptionId:", prescriptionId);
      await dispatch(getFollowupListAction(prescriptionId));
    } catch (error) {
      console.error("Error fetching followup data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load followup data",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (prescriptionId) {
      fetchFollowupData();
    }
  }, [prescriptionId]);

  // Function to handle accordion selection
  const handleAccordionSelect = (eventKey) => {
    setActiveAccordion(eventKey);
  };

  if (followupListLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (followupListError) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to load followup data. Please try again.
      </div>
    );
  }

  return (
    <div className="mb-4">
      <h5 className="fw-bold mb-3">Past Follow-ups</h5>
      {followupListData.length === 0 ? (
        <div className="alert alert-info">No follow-up data available.</div>
      ) : (
        <Accordion
          activeKey={activeAccordion}
          onSelect={handleAccordionSelect}
          className="accordion accordion-primary"
        >
          {followupListData.map((followup, index) => (
            <Accordion.Item key={index} eventKey={index.toString()}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                  {/* Left side: Date + Follow‑up numbers */}
                  <div style={{ fontSize: "14px", fontWeight: "600" }}>
  <span>Date: {formatDateTime(followup.created_at)}</span>

  {followup.followup_no && (
    <span> | Follow-up #{followup.followup_no}</span>
  )}

  {followup.service_call_no && (
    <span> | Service Call #{followup.service_call_no}</span>
  )}

  {/* Schedule Details */}
  {followup.schedule_details && (
  <>
  {followup.schedule_details.followup_count && (
    <span className="ms-1">
      | {followup.schedule_details.followup_count}
    </span>
  )}

  {followup.schedule_details.followup_date && (
    <span className="ms-1">
     
      {formatDateTime(followup.schedule_details.followup_date)}
    </span>
  )}
</>
  )}
</div>

                  {/* Right side: Badges */}
                  <div className="me-4">
                    {followup.is_intermediate === "Yes" ? (
                      <span className="badge bg-info me-2">Intermediate</span>
                    ) : followup.is_intermediate === "No" ? (
                      <span className="badge bg-secondary me-2">Scheduled</span>
                    ) : null}
                    <span className="badge bg-warning me-2">
                      {followup.questionnaire?.length || 0} Questions
                    </span>
                    <span className="badge bg-info">
                      {followup.notes?.length || 0} Notes
                    </span>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {/* Questionnaire Section */}
                {followup.questionnaire &&
                  followup.questionnaire.length > 0 && (
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3">Questionnaire</h6>
                      {followup.questionnaire.map((item, qIndex) => {
                        return item.isGroup === "No" ? (
                          <div
                            key={qIndex}
                            className="mb-3 p-3 border rounded"
                            style={{
                              backgroundColor: item.is_negative === "Yes" ? "#ffe6e6" : "white",
                              borderLeft: item.is_negative === "Yes" ? "4px solid #ff4d4d" : "none"
                            }}
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong className="text-primary">
                                  {item.question}
                                </strong>
                                {item.short_form && item.short_form !== "null" && (
                                  <div className="text-muted small mt-1">
                                    {item.short_form}
                                  </div>
                                )}
                              </div>
                              <div>
                                {item.is_important === "Yes" && (
                                  <span className="badge bg-warning me-1">
                                    Important
                                  </span>
                                )}
                                {item.is_negative === "Yes" && (
                                  <span className="badge bg-danger">
                                    Negative
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Use renderAnswer function for file handling */}
                            {renderAnswer(item.answer, item.question_type)}
                            {item.disease_status && item.disease_status !== "" && (
                              <div className="mt-2">
                                <small className="text-muted">
                                  Disease Status: <strong>{item.disease_status}</strong>
                                </small>
                              </div>
                            )}
                            {item.answer_note.length > 0 && (
                              <div className="mt-2 p-2 text-dark custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm">
                                <small>Note: {item.answer_note[0]?.note_description}</small>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div
                            key={qIndex}
                            className="mb-3 p-3 border rounded"
                          >
                            <div className="justify-content-between">
                              <strong className="text-primary">
                                {item.group_name}
                              </strong>
                              {item.questions.map((question, qaIndex) => (
                                <div 
                                  key={qaIndex} 
                                  className="mb-2 mt-4 p-2 border rounded"
                                  style={{
                                    backgroundColor: question.is_negative === "Yes" ? "#ffe6e6" : "white",
                                    borderLeft: question.is_negative === "Yes" ? "4px solid #ff4d4d" : "none"
                                  }}
                                >
                                  <div className="d-flex justify-content-between">
                                    <strong>{question.question}</strong>
                                    <div>
                                      {question.is_important === "Yes" && (
                                        <span className="badge bg-warning me-1">
                                          Important
                                        </span>
                                      )}
                                      {question.is_negative === "Yes" && (
                                        <span className="badge bg-danger">
                                          Negative
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {question.short_form && question.short_form !== "null" && (
                                    <div className="text-muted small mt-1">
                                      {question.short_form}
                                    </div>
                                  )}
                                  {/* Use renderAnswer function for file handling */}
                                  {renderAnswer(question.answer, question.question_type)}
                                  {question.disease_status && question.disease_status !== "" && (
                                    <div className="mt-2">
                                      <small className="text-muted">
                                        Disease Status: <strong>{question.disease_status}</strong>
                                      </small>
                                    </div>
                                  )}
                                  {question.answer_note.length > 0 && (
                                    <div className=" text-dark custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm">
                                      <small>Note: {question.answer_note[0]?.note_description}</small>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                {/* Notes Section */}
                {followup.notes && followup.notes.length > 0 && (
                  <div>
                    <h6 className="fw-bold mb-3">Notes</h6>
                    {followup.notes.map((note, nIndex) => (
                      <div
                        key={nIndex}
                        className="mb-3 custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          {/* LEFT CONTENT */}
                          <div className="flex-grow-1">
                            <p className="text-dark mb-2" style={{ fontSize: "15px" }}>
                              {note.note_description}
                            </p>
                            <small className="text-dark" style={{ fontSize: "13px" }}>
                              Type: {note.note_type}
                            </small>
                          </div>
                          {/* BADGES */}
                          <div className="ms-3">
                            {note.is_important === "Yes" && (
                              <span className="badge bg-warning text-dark me-1">
                                Important
                              </span>
                            )}
                            {note.is_emergency === "Yes" && (
                              <span className="badge bg-danger">
                                Emergency
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </div>
  );
};

QuestionnaireSection.propTypes = {
  prescriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default QuestionnaireSection;